"use strict";
/**
 * Unified Error Codes and Types for Vitly Application
 * Shared across Backend, Frontend, and Mobile platforms
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.ERROR_CODES = exports.ErrorSeverity = exports.ErrorCategory = void 0;
exports.getErrorDefinition = getErrorDefinition;
exports.isRetryableError = isRetryableError;
exports.getHttpStatus = getHttpStatus;
exports.getUserMessage = getUserMessage;
exports.getErrorCategory = getErrorCategory;
exports.getErrorSeverity = getErrorSeverity;
// Error Categories
var ErrorCategory;
(function (ErrorCategory) {
    ErrorCategory["NETWORK"] = "NETWORK";
    ErrorCategory["AUTHENTICATION"] = "AUTHENTICATION";
    ErrorCategory["AUTHORIZATION"] = "AUTHORIZATION";
    ErrorCategory["VALIDATION"] = "VALIDATION";
    ErrorCategory["BUSINESS_LOGIC"] = "BUSINESS_LOGIC";
    ErrorCategory["DATABASE"] = "DATABASE";
    ErrorCategory["EXTERNAL_SERVICE"] = "EXTERNAL_SERVICE";
    ErrorCategory["SYSTEM"] = "SYSTEM";
    ErrorCategory["RATE_LIMIT"] = "RATE_LIMIT";
    ErrorCategory["SUBSCRIPTION"] = "SUBSCRIPTION";
})(ErrorCategory || (exports.ErrorCategory = ErrorCategory = {}));
// Error Severity Levels
var ErrorSeverity;
(function (ErrorSeverity) {
    ErrorSeverity["LOW"] = "LOW";
    ErrorSeverity["MEDIUM"] = "MEDIUM";
    ErrorSeverity["HIGH"] = "HIGH";
    ErrorSeverity["CRITICAL"] = "CRITICAL";
})(ErrorSeverity || (exports.ErrorSeverity = ErrorSeverity = {}));
// Unified Error Codes with consistent structure
exports.ERROR_CODES = {
    // Network Errors
    NETWORK_ERROR: {
        code: 'NETWORK_ERROR',
        category: ErrorCategory.NETWORK,
        severity: ErrorSeverity.MEDIUM,
        httpStatus: 0,
        message: 'Network connection failed',
        userMessage: 'Please check your internet connection and try again.',
        retryable: true
    },
    TIMEOUT_ERROR: {
        code: 'TIMEOUT_ERROR',
        category: ErrorCategory.NETWORK,
        severity: ErrorSeverity.MEDIUM,
        httpStatus: 408,
        message: 'Request timed out',
        userMessage: 'The request took too long. Please try again.',
        retryable: true
    },
    CONNECTION_LOST: {
        code: 'CONNECTION_LOST',
        category: ErrorCategory.NETWORK,
        severity: ErrorSeverity.MEDIUM,
        httpStatus: 0,
        message: 'Connection lost',
        userMessage: 'Connection lost. Please check your network and try again.',
        retryable: true
    },
    // Authentication Errors
    UNAUTHORIZED: {
        code: 'UNAUTHORIZED',
        category: ErrorCategory.AUTHENTICATION,
        severity: ErrorSeverity.MEDIUM,
        httpStatus: 401,
        message: 'Authentication required',
        userMessage: 'You are not authorized to perform this action.',
        retryable: false
    },
    FORBIDDEN: {
        code: 'FORBIDDEN',
        category: ErrorCategory.AUTHORIZATION,
        severity: ErrorSeverity.MEDIUM,
        httpStatus: 403,
        message: 'Access denied',
        userMessage: 'Access denied. You do not have permission to access this resource.',
        retryable: false
    },
    TOKEN_EXPIRED: {
        code: 'TOKEN_EXPIRED',
        category: ErrorCategory.AUTHENTICATION,
        severity: ErrorSeverity.MEDIUM,
        httpStatus: 401,
        message: 'Authentication token expired',
        userMessage: 'Your session has expired. Please log in again.',
        retryable: false
    },
    INVALID_TOKEN: {
        code: 'INVALID_TOKEN',
        category: ErrorCategory.AUTHENTICATION,
        severity: ErrorSeverity.MEDIUM,
        httpStatus: 401,
        message: 'Invalid authentication token',
        userMessage: 'Your session is invalid. Please log in again.',
        retryable: false
    },
    INVALID_CREDENTIALS: {
        code: 'INVALID_CREDENTIALS',
        category: ErrorCategory.AUTHENTICATION,
        severity: ErrorSeverity.MEDIUM,
        httpStatus: 401,
        message: 'Invalid email or password',
        userMessage: 'Invalid email or password. Please try again.',
        retryable: false
    },
    // Validation Errors
    VALIDATION_ERROR: {
        code: 'VALIDATION_ERROR',
        category: ErrorCategory.VALIDATION,
        severity: ErrorSeverity.LOW,
        httpStatus: 400,
        message: 'Invalid request data',
        userMessage: 'Please check your input and try again.',
        retryable: false
    },
    REQUIRED_FIELD: {
        code: 'REQUIRED_FIELD',
        category: ErrorCategory.VALIDATION,
        severity: ErrorSeverity.LOW,
        httpStatus: 400,
        message: 'Required field is missing',
        userMessage: 'This field is required.',
        retryable: false
    },
    INVALID_FORMAT: {
        code: 'INVALID_FORMAT',
        category: ErrorCategory.VALIDATION,
        severity: ErrorSeverity.LOW,
        httpStatus: 400,
        message: 'Invalid data format',
        userMessage: 'Please enter a valid value.',
        retryable: false
    },
    INVALID_EMAIL: {
        code: 'INVALID_EMAIL',
        category: ErrorCategory.VALIDATION,
        severity: ErrorSeverity.LOW,
        httpStatus: 400,
        message: 'Invalid email format',
        userMessage: 'Please enter a valid email address.',
        retryable: false
    },
    INVALID_PHONE: {
        code: 'INVALID_PHONE',
        category: ErrorCategory.VALIDATION,
        severity: ErrorSeverity.LOW,
        httpStatus: 400,
        message: 'Invalid phone number format',
        userMessage: 'Please enter a valid phone number.',
        retryable: false
    },
    // Business Logic Errors
    NOT_FOUND: {
        code: 'NOT_FOUND',
        category: ErrorCategory.BUSINESS_LOGIC,
        severity: ErrorSeverity.LOW,
        httpStatus: 404,
        message: 'Resource not found',
        userMessage: 'The requested resource was not found.',
        retryable: false
    },
    ALREADY_EXISTS: {
        code: 'ALREADY_EXISTS',
        category: ErrorCategory.BUSINESS_LOGIC,
        severity: ErrorSeverity.LOW,
        httpStatus: 409,
        message: 'Resource already exists',
        userMessage: 'This item already exists.',
        retryable: false
    },
    INSUFFICIENT_PERMISSIONS: {
        code: 'INSUFFICIENT_PERMISSIONS',
        category: ErrorCategory.AUTHORIZATION,
        severity: ErrorSeverity.MEDIUM,
        httpStatus: 403,
        message: 'Insufficient permissions',
        userMessage: 'You do not have sufficient permissions.',
        retryable: false
    },
    OPERATION_NOT_ALLOWED: {
        code: 'OPERATION_NOT_ALLOWED',
        category: ErrorCategory.BUSINESS_LOGIC,
        severity: ErrorSeverity.MEDIUM,
        httpStatus: 403,
        message: 'Operation not allowed',
        userMessage: 'This operation is not allowed.',
        retryable: false
    },
    RESOURCE_CONFLICT: {
        code: 'RESOURCE_CONFLICT',
        category: ErrorCategory.BUSINESS_LOGIC,
        severity: ErrorSeverity.MEDIUM,
        httpStatus: 409,
        message: 'Resource conflict',
        userMessage: 'There is a conflict with the current state.',
        retryable: false
    },
    MEETUP_FULL: {
        code: 'MEETUP_FULL',
        category: ErrorCategory.BUSINESS_LOGIC,
        severity: ErrorSeverity.LOW,
        httpStatus: 409,
        message: 'Meetup is full',
        userMessage: 'This meetup is already full.',
        retryable: false
    },
    MEETUP_CANCELLED: {
        code: 'MEETUP_CANCELLED',
        category: ErrorCategory.BUSINESS_LOGIC,
        severity: ErrorSeverity.LOW,
        httpStatus: 410,
        message: 'Meetup has been cancelled',
        userMessage: 'This meetup has been cancelled.',
        retryable: false
    },
    // Database Errors
    DATABASE_ERROR: {
        code: 'DATABASE_ERROR',
        category: ErrorCategory.DATABASE,
        severity: ErrorSeverity.HIGH,
        httpStatus: 500,
        message: 'Database operation failed',
        userMessage: 'Database error. Please try again later.',
        retryable: true
    },
    CONNECTION_ERROR: {
        code: 'CONNECTION_ERROR',
        category: ErrorCategory.DATABASE,
        severity: ErrorSeverity.HIGH,
        httpStatus: 500,
        message: 'Database connection failed',
        userMessage: 'Connection error. Please try again later.',
        retryable: true
    },
    QUERY_TIMEOUT: {
        code: 'QUERY_TIMEOUT',
        category: ErrorCategory.DATABASE,
        severity: ErrorSeverity.MEDIUM,
        httpStatus: 500,
        message: 'Database query timed out',
        userMessage: 'Request timed out. Please try again.',
        retryable: true
    },
    // External Service Errors
    EXTERNAL_SERVICE_ERROR: {
        code: 'EXTERNAL_SERVICE_ERROR',
        category: ErrorCategory.EXTERNAL_SERVICE,
        severity: ErrorSeverity.MEDIUM,
        httpStatus: 502,
        message: 'External service unavailable',
        userMessage: 'External service unavailable. Please try again later.',
        retryable: true
    },
    GOOGLE_MAPS_ERROR: {
        code: 'GOOGLE_MAPS_ERROR',
        category: ErrorCategory.EXTERNAL_SERVICE,
        severity: ErrorSeverity.MEDIUM,
        httpStatus: 502,
        message: 'Google Maps service error',
        userMessage: 'Location service temporarily unavailable.',
        retryable: true
    },
    PAYMENT_SERVICE_ERROR: {
        code: 'PAYMENT_SERVICE_ERROR',
        category: ErrorCategory.EXTERNAL_SERVICE,
        severity: ErrorSeverity.HIGH,
        httpStatus: 502,
        message: 'Payment service error',
        userMessage: 'Payment service temporarily unavailable.',
        retryable: true
    },
    NOTIFICATION_SERVICE_ERROR: {
        code: 'NOTIFICATION_SERVICE_ERROR',
        category: ErrorCategory.EXTERNAL_SERVICE,
        severity: ErrorSeverity.MEDIUM,
        httpStatus: 502,
        message: 'Notification service error',
        userMessage: 'Notification service temporarily unavailable.',
        retryable: false
    },
    // Subscription Errors
    SUBSCRIPTION_REQUIRED: {
        code: 'SUBSCRIPTION_REQUIRED',
        category: ErrorCategory.SUBSCRIPTION,
        severity: ErrorSeverity.MEDIUM,
        httpStatus: 402,
        message: 'Subscription required',
        userMessage: 'This feature requires a subscription.',
        retryable: false
    },
    SUBSCRIPTION_EXPIRED: {
        code: 'SUBSCRIPTION_EXPIRED',
        category: ErrorCategory.SUBSCRIPTION,
        severity: ErrorSeverity.MEDIUM,
        httpStatus: 402,
        message: 'Subscription expired',
        userMessage: 'Your subscription has expired.',
        retryable: false
    },
    SUBSCRIPTION_LIMIT_EXCEEDED: {
        code: 'SUBSCRIPTION_LIMIT_EXCEEDED',
        category: ErrorCategory.SUBSCRIPTION,
        severity: ErrorSeverity.MEDIUM,
        httpStatus: 402,
        message: 'Subscription limit exceeded',
        userMessage: 'You have reached your subscription limit.',
        retryable: false
    },
    PAYMENT_FAILED: {
        code: 'PAYMENT_FAILED',
        category: ErrorCategory.SUBSCRIPTION,
        severity: ErrorSeverity.MEDIUM,
        httpStatus: 402,
        message: 'Payment failed',
        userMessage: 'Payment failed. Please try again.',
        retryable: true
    },
    // Rate Limiting
    RATE_LIMIT_EXCEEDED: {
        code: 'RATE_LIMIT_EXCEEDED',
        category: ErrorCategory.RATE_LIMIT,
        severity: ErrorSeverity.MEDIUM,
        httpStatus: 429,
        message: 'Rate limit exceeded',
        userMessage: 'Too many requests. Please wait and try again.',
        retryable: true
    },
    // System Errors
    INTERNAL_ERROR: {
        code: 'INTERNAL_ERROR',
        category: ErrorCategory.SYSTEM,
        severity: ErrorSeverity.HIGH,
        httpStatus: 500,
        message: 'Internal server error',
        userMessage: 'An unexpected error occurred. Please try again later.',
        retryable: true
    },
    SERVICE_UNAVAILABLE: {
        code: 'SERVICE_UNAVAILABLE',
        category: ErrorCategory.SYSTEM,
        severity: ErrorSeverity.HIGH,
        httpStatus: 503,
        message: 'Service temporarily unavailable',
        userMessage: 'Service temporarily unavailable. Please try again later.',
        retryable: true
    },
    MAINTENANCE_MODE: {
        code: 'MAINTENANCE_MODE',
        category: ErrorCategory.SYSTEM,
        severity: ErrorSeverity.LOW,
        httpStatus: 503,
        message: 'System under maintenance',
        userMessage: 'System is under maintenance. Please try again later.',
        retryable: false
    }
};
// Helper functions
function getErrorDefinition(code) {
    return exports.ERROR_CODES[code];
}
function isRetryableError(code) {
    return exports.ERROR_CODES[code].retryable;
}
function getHttpStatus(code) {
    return exports.ERROR_CODES[code].httpStatus;
}
function getUserMessage(code) {
    return exports.ERROR_CODES[code].userMessage;
}
function getErrorCategory(code) {
    return exports.ERROR_CODES[code].category;
}
function getErrorSeverity(code) {
    return exports.ERROR_CODES[code].severity;
}
